import {useQuery} from '@tanstack/react-query';
import React from 'react';

import {AssetVideo, VideoLecture as VideoLectureType} from 'src/generated/graphql';
import {fetcher} from 'src/lib/gql-fetcher';

interface LecturesQuery {
    lectures?: Array<VideoLecture | null> | null;
}

export interface VideoLecture extends Omit<VideoLectureType, 'asset'> {
    asset: AssetVideo;
}

export interface UseLecturesByIdsResult {
    count: number;
    isLoading: boolean;
    lectures: VideoLecture[];
}

const isValidVideoLecture = (lecture: VideoLecture | null): lecture is VideoLecture => {
    if (!lecture) return false;

    const hasId = 'id' in lecture && typeof lecture.id === 'string';
    const hasDuration =
        'durationInSeconds' in lecture && typeof lecture.durationInSeconds === 'number';
    const isPreviewable = 'isPreviewable' in lecture && lecture.isPreviewable === true;

    return hasId && hasDuration && isPreviewable;
};

const document = `
    fragment VideoLectureAssetDetails on AssetVideo {
        id
        title
        duration
        status
        processingErrors {
            errorMessage
            helpLink
        }
        accessToken
        isDrmProtected
        mediaSources {
            label
            src
            mediaType
        }
        downloads {
            label
            urlFile
        }
        captions {
            id
            fileName
            url
            language
            title
            videoLabel
            source
            status
        }
        thumbnailTrack {
            vttUrl
            imgUrl
        }
    }

    query Lectures($ids: [ID!]!) {
        lectures(ids: $ids) {
            id
            title
            thumbnail
            __typename
            ... on VideoLecture {
                isPreviewable
                durationInSeconds
                thumbnail
                asset {
                    __typename
                    ...VideoLectureAssetDetails
                }
            }
        }
    }
`;

/**
 * Returns previewable video lectures for the provided IDs.
 */
export const useLecturesByIds = (lectureIds?: string[]): UseLecturesByIdsResult => {
    const {data, isLoading} = useQuery({
        queryKey: ['Lectures', {ids: lectureIds ?? []}],
        queryFn: () =>
            fetcher<LecturesQuery, {ids: string[]}>(document, {
                ids: lectureIds ?? [],
            })(),
        enabled: Boolean(lectureIds?.length),
        select: (data) => data.lectures?.filter(isValidVideoLecture),
    });

    const count = Number(lectureIds?.length ?? 0);

    return React.useMemo(
        () => ({
            count,
            isLoading,
            lectures: data ?? [],
        }),
        [isLoading, data, count],
    );
};
